import { useCallback, useEffect, useRef, useState } from "react";

import debounce from "lodash-es/debounce";
import log from "logbeacon";
import { useShellRouter } from "@/src/hooks";
import { useSnapshot } from "valtio";
import { usePortfolioContext } from "@/src/components/Portfolio/PortfolioContext";
import { ROUTER_PATH } from "@/src/config";
import { PortfolioAsset } from "@/src/features/api/openData";
import { favoritesCenter, rootStore } from "@/src/store";

export type SearchResult = PortfolioAsset;

/**
 * 搜索业务逻辑 Hook
 * - 管理搜索关键词和下拉显示状态
 * - 从收藏夹获取资产数据并提供过滤逻辑
 * - 处理输入变更和清空操作
 */
export interface UseSearchLogicProps {
  onSelect?: (asset: PortfolioAsset) => void;
  onClear?: () => void;
  portfolioId: string;
}

export const useSearchLogic = ({
  onSelect,
  onClear,
  portfolioId,
}: UseSearchLogicProps) => {
  const router = useShellRouter();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { keywords, setKeywords, setSearchAsset, searchAsset } =
    usePortfolioContext();
  const [showDropdown, setShowDropdown] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const DEBOUNCE_MS = 300;
  // 使用 any 以兼容 lodash 的 DebouncedFunc 类型（避免类型包缺失导致的报错）
  const debouncedFilterRef = useRef<any>(null);

  const [hasMore, setHasMore] = useState(true);
  const [nextToken, setNextToken] = useState<string | undefined>();
  const PAGE_SIZE = 20;

  // 获取收藏夹内的资产数据
  const fetchAssets = useCallback(
    async (isRefresh = false) => {
      try {
        setLoading(true);
        const result = await favoritesCenter.fetchFolderContents(portfolioId, {
          limit: PAGE_SIZE,
          next_token: isRefresh ? undefined : nextToken,
        });

        if (result.success && result.data) {
          const newFolders = result.data;

          setResults((prev) =>
            isRefresh ? newFolders : [...prev, ...newFolders],
          );
          setHasMore(!!result.next_token);
          setNextToken(result.next_token);
          return true;
        }
        return false;
      } catch (error) {
        log.error("[文案]Failed to fetch folders:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [favoritesCenter, keywords, nextToken, portfolioId],
  );

  // 加载更多数据
  const loadMore = async () => {
    if (!hasMore || loading) {
      return;
    }
    await fetchAssets(false);
  };

  // 初始化 debounce 函数并在卸载时清理
  useEffect(() => {
    debouncedFilterRef.current = debounce(async (q: string) => {
      if (!q) {
        setResults([]);
        setLoading(false);
        return;
      }
      await fetchAssets(true);
    }, DEBOUNCE_MS);
    return () => {
      debouncedFilterRef.current?.cancel();
      debouncedFilterRef.current = null;
    };
  }, [fetchAssets]);

  // 关键词变化时触发防抖搜索
  useEffect(() => {
    const q = keywords.trim().toLowerCase();
    setLoading(true);
    debouncedFilterRef.current?.(q);
  }, [keywords]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isLoggedIn) {
      router.replace(ROUTER_PATH.HOME);
    }
    const value = e.target.value;
    setKeywords(value);
    setShowDropdown(!!value.trim());
  };

  const handleSelect = (item: SearchResult) => {
    setSearchAsset(item); // 这会自动设置 keywords
    setShowDropdown(false);

    // 调用外部的选择回调
    if (onSelect) {
      onSelect(item);
    }
  };

  // 重置搜索结果状态（不清除关键词）
  const resetResultsState = useCallback(() => {
    setShowDropdown(false);
    setResults([]);
    setLoading(false);
    setHasMore(true);
    setNextToken(undefined);
  }, []);

  // 完全重置搜索状态（包括关键词）
  const resetAllSearchState = useCallback(() => {
    setSearchAsset(null); // 使用 clearSearchAsset 来同时清除 keywords 和 searchAsset
    setKeywords("");
    resetResultsState();
  }, [resetResultsState]);

  const handleCloseDropdown = () => {
    // 检查当前输入是否匹配搜索结果或已选中的资产
    const currentKeyword = keywords.trim().toLowerCase();
    const matchesResults = results.some(
      (item) => item.asset_symbol?.toLowerCase() === currentKeyword,
    );
    const matchesSelected =
      searchAsset?.asset_symbol?.toLowerCase() === currentKeyword;

    if (!matchesResults && !matchesSelected) {
      // 如果既不匹配搜索结果也不匹配已选中资产，清除搜索条件
      resetAllSearchState();
    } else {
      // 如果匹配，只重置结果状态（保留搜索条件）
      resetResultsState();
    }
  };

  const clearSearch = () => {
    resetAllSearchState(); // 完全重置，包括关键词
    onClear?.();
  };

  return {
    showDropdown,
    setShowDropdown,
    results,
    loading,
    hasMore,
    loadMore,
    handleInputChange,
    handleSelect,
    handleCloseDropdown,
    clearSearch,
  };
};
